import type { AdmissionFeeHead, ConcessionRequest } from '../types/admissions';

/**
 * Admission-fee rules on the client — mirrors
 * backend/src/services/admissions/admissionFees.ts so the dialogs preview
 * exactly what the server will charge.
 */

/** Heads an applicant pays: their class (or all classes), optional heads
 *  only when opted into, zero amounts dropped. */
export const applicableAdmissionFeeHeads = (
  heads: AdmissionFeeHead[] | undefined,
  classId: string | null | undefined,
  optionalLabels: readonly string[] = [],
): AdmissionFeeHead[] =>
  (heads || []).filter(h =>
    (!h.classId || String(h.classId) === String(classId))
    && (!h.optional || optionalLabels.includes(h.label))
    && h.amount > 0,
  );

/** Optional heads offered to this class (the parent may opt in). */
export const optionalAdmissionFeeHeads = (
  heads: AdmissionFeeHead[] | undefined,
  classId: string | null | undefined,
): AdmissionFeeHead[] =>
  (heads || []).filter(h => h.optional && h.amount > 0 && (!h.classId || String(h.classId) === String(classId)));

export const sumFeeHeads = (heads: AdmissionFeeHead[]): number =>
  heads.reduce((s, h) => s + h.amount, 0);

/** Rupee value of a concession against a base amount (capped to the base). */
export const concessionValue = (c: Pick<ConcessionRequest, 'mode' | 'value'>, base: number): number =>
  Math.round(Math.min(base, c.mode === 'percent' ? (base * c.value) / 100 : c.value) * 100) / 100;

/** "10%" / "Rs 5,000" */
export const concessionAmountLabel = (c: Pick<ConcessionRequest, 'mode' | 'value'>): string =>
  c.mode === 'percent' ? `${c.value}%` : `Rs ${c.value.toLocaleString()}`;

/** Error payload returned when a fee step blocks a status change. */
export interface FeeGateError {
  code: 'FEE_UNSETTLED';
  kind: 'application_fee' | 'admission_fee';
  invoice: { invoiceId: string; invoiceNumber: string; outstanding: number } | null;
  canOverride: boolean;
}

/** The fee-gate details from a failed transition, or null for other errors. */
export const feeGateFromError = (err: any): FeeGateError | null =>
  err?.data?.code === 'FEE_UNSETTLED' ? (err.data as FeeGateError) : null;
